import { useMemo, useState } from 'react'
import { Check, Search, X } from 'lucide-react'
import { useTaxonomyTree } from '../../api/queries/taxonomy'
import { flattenCategories } from '../../lib/thriftCategories'
import { MobileSearchPill } from '../search/MobileSearchPill'
import { SearchSheet } from '../search/SearchSheet'
import { SearchSection } from '../search/SearchSection'
import type { ThriftSearch } from './ThriftSearchContext'

interface CategoryOption {
  id: string
  label: string
}

/**
 * Airbnb-inspired "Item / Category" search for thrift discovery. On desktop (sm+)
 * an inline pill; on mobile a compact "Start your search" pill that opens a
 * full-screen {@link SearchSheet} with stacked What / Category sections.
 * Controlled — the frame owns the state so the results grid reads the same query.
 * Desktop filtering is live; the mobile sheet commits on Search.
 *
 * `deferred` is for the pages that carry the pill but no results — an item's detail
 * page, where committing navigates to discovery. Filtering live from there would
 * leave on the first keystroke and take the input's focus with it, so the desktop
 * pill holds a draft and commits on Enter or the search button instead. The mobile
 * sheet already works that way and is unaffected.
 */
export function ThriftSearchBar({
  search,
  onChange,
  deferred = false,
}: {
  search: ThriftSearch
  onChange: (next: ThriftSearch) => void
  deferred?: boolean
}) {
  const { data: categoryTree } = useTaxonomyTree(undefined, true, 'thrifts')
  const categoryOptions = useMemo<CategoryOption[]>(
    () => (categoryTree ? flattenCategories(categoryTree) : []),
    [categoryTree],
  )

  // In deferred mode the desktop fields edit a draft; `search` is still what the rest
  // of the app has committed, and re-seeds the draft if it changes underneath.
  const [draft, setDraft] = useState<ThriftSearch>(search)
  const [committed, setCommitted] = useState<ThriftSearch>(search)
  if (committed !== search) {
    setCommitted(search)
    setDraft(search)
  }
  const value = deferred ? draft : search
  const update = (next: ThriftSearch) => (deferred ? setDraft(next) : onChange(next))

  const categoryLabel = useMemo(
    () => categoryOptions.find((c) => c.id === value.categoryId)?.label ?? '',
    [categoryOptions, value.categoryId],
  )
  const hasFilters = value.name.trim() !== '' || value.categoryId !== ''

  const [sheetOpen, setSheetOpen] = useState(false)
  const summary = [value.name.trim(), categoryLabel].filter(Boolean).join(' · ')

  return (
    <>
      {/* Mobile: collapsed pill → full-screen sheet */}
      <div className="sm:hidden">
        <MobileSearchPill
          summary={summary}
          onClick={() => setSheetOpen(true)}
          ariaLabel="Search thrifts"
        />
      </div>
      {sheetOpen && (
        <ThriftSearchSheet
          search={value}
          categoryOptions={categoryOptions}
          onChange={onChange}
          onClose={() => setSheetOpen(false)}
        />
      )}

      {/* Desktop: inline pill */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (deferred) onChange(draft)
        }}
        className="mx-auto hidden w-full max-w-2xl flex-row items-center gap-1 rounded-full border border-stone-200 bg-white p-1.5 shadow-[0_3px_14px_rgba(28,25,23,0.10)] transition focus-within:border-brand-400 focus-within:shadow-[0_4px_20px_rgba(28,25,23,0.14)] sm:flex"
      >
        {/* Item */}
        <label className="min-w-0 flex-1 cursor-text rounded-full px-4 py-2 transition hover:bg-stone-50">
          <span className="block text-[11px] font-bold uppercase tracking-wide text-stone-700">
            Item
          </span>
          <input
            value={value.name}
            onChange={(e) => update({ ...value, name: e.target.value })}
            placeholder="Search by name"
            aria-label="Search thrifts by name"
            className="w-full bg-transparent text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none"
          />
        </label>

        <span className="h-8 w-px shrink-0 bg-stone-200" />

        {/* Category */}
        <label className="min-w-0 flex-1 cursor-pointer rounded-full px-4 py-2 transition hover:bg-stone-50">
          <span className="block text-[11px] font-bold uppercase tracking-wide text-stone-700">
            Category
          </span>
          <select
            value={value.categoryId}
            onChange={(e) => update({ ...value, categoryId: e.target.value })}
            aria-label="Filter thrifts by category"
            className="w-full cursor-pointer bg-transparent text-sm text-stone-700 focus:outline-none"
          >
            <option value="">All categories</option>
            {categoryOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </label>

        {deferred ? (
          // Nothing on this page to clear — the button's job here is to take the
          // draft to the results.
          <button
            type="submit"
            aria-label="Search"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-700 text-white transition hover:bg-brand-800"
          >
            <Search className="h-5 w-5" />
          </button>
        ) : hasFilters ? (
          <button
            type="button"
            onClick={() => onChange({ name: '', categoryId: '' })}
            aria-label="Clear search"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-stone-200 text-stone-500 transition hover:bg-stone-50 hover:text-stone-700"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          <span
            aria-hidden="true"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-700 text-white"
          >
            <Search className="h-5 w-5" />
          </span>
        )}
      </form>
    </>
  )
}

/**
 * Full-screen mobile search sheet with stacked What / Category sections. Mounted
 * only while open (by the caller), so the draft seeds from committed `search` and
 * the first field re-arms on each open. Edits a draft and commits on Search,
 * matching the homestay sheet.
 */
function ThriftSearchSheet({
  search,
  categoryOptions,
  onChange,
  onClose,
}: {
  search: ThriftSearch
  categoryOptions: CategoryOption[]
  onChange: (next: ThriftSearch) => void
  onClose: () => void
}) {
  const [draft, setDraft] = useState<ThriftSearch>(search)
  const [section, setSection] = useState<'what' | 'category'>('what')

  const draftCategoryLabel =
    categoryOptions.find((c) => c.id === draft.categoryId)?.label ?? 'All categories'

  return (
    <SearchSheet
      open
      onClose={onClose}
      onClear={() => setDraft({ name: '', categoryId: '' })}
      onSubmit={() => {
        onChange(draft)
        onClose()
      }}
      title="Search thrifts"
    >
      <SearchSection
        label="What"
        heading="What are you after?"
        summary={draft.name.trim() || 'Any item'}
        expanded={section === 'what'}
        onExpand={() => setSection('what')}
      >
        <label className="flex items-center gap-3 rounded-xl border border-stone-300 px-4 py-3 focus-within:border-stone-900">
          <Search className="h-4 w-4 shrink-0 text-stone-500" />
          <input
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            placeholder="Search by name"
            aria-label="Search thrifts by name"
            className="w-full bg-transparent text-base text-stone-900 placeholder:text-stone-400 focus:outline-none"
          />
        </label>
      </SearchSection>

      <SearchSection
        label="Category"
        heading="Category"
        summary={draftCategoryLabel}
        expanded={section === 'category'}
        onExpand={() => setSection('category')}
      >
        <ul className="max-h-[46vh] space-y-1 overflow-y-auto">
          <CategoryRow
            label="All categories"
            selected={draft.categoryId === ''}
            onSelect={() => setDraft((d) => ({ ...d, categoryId: '' }))}
          />
          {categoryOptions.map((c) => (
            <CategoryRow
              key={c.id}
              label={c.label}
              selected={draft.categoryId === c.id}
              onSelect={() => setDraft((d) => ({ ...d, categoryId: c.id }))}
            />
          ))}
        </ul>
      </SearchSection>
    </SearchSheet>
  )
}

function CategoryRow({
  label,
  selected,
  onSelect,
}: {
  label: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={`flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-left text-sm transition ${
          selected
            ? 'bg-stone-100 font-semibold text-stone-900'
            : 'text-stone-700 hover:bg-stone-50'
        }`}
      >
        <span className="truncate">{label}</span>
        {selected ? <Check className="h-4 w-4 shrink-0 text-brand-700" /> : null}
      </button>
    </li>
  )
}
